/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { StaggeredEntrance } from '@atlaskit/motion';
import { Box, Flex, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { PlaylistItem, type PlaylistItemData } from './PlaylistItem';
import { SuggestedEmptyView } from './SuggestedEmptyView';

const messages = defineMessages({
	suggested: {
		id: 'ai-audio.playlist-suggested.header',
		defaultMessage: 'Suggested',
		description: 'Header for the suggested items section in the playlist',
	},
} as const);

const styles = cssMap({
	playlist: {
		flex: 1,
		paddingTop: token('space.200'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	sectionHeader: {
		textAlign: 'left',
		paddingLeft: token('space.100'),
		paddingBottom: token('space.100'),
		paddingRight: token('space.050'),
		color: token('color.text.subtle'),
	},
	sectionList: {
		listStyleType: 'none',
		margin: '0',
		padding: '0',
	},
});

interface SuggestedViewProps {
	suggestedItems: PlaylistItemData[];
}

export const SuggestedView = ({ suggestedItems }: SuggestedViewProps) => {
	const { formatMessage } = useIntl();

	const renderSuggestedItem = useCallback(
		(item: PlaylistItemData, index: number) => (
			<li key={item.id}>
				<PlaylistItem
					data={item}
					isFirstItem={false}
					isLastItem={false}
					currentIndex={index}
					allItems={suggestedItems}
				/>
			</li>
		),
		[suggestedItems],
	);

	return (
		<StaggeredEntrance columns={1}>
			<Flex xcss={styles.playlist} direction="column">
				<Box xcss={styles.sectionHeader}>
					<Heading as="h4" size="small">
						{formatMessage(messages.suggested)}
					</Heading>
				</Box>
				<Flex xcss={styles.playlist} direction="column">
					{suggestedItems.length > 0 ? (
						<Stack as="ul" space="space.100" xcss={styles.sectionList}>
							{suggestedItems.map((item, index) => {
								return renderSuggestedItem(item, index + 1);
							})}
						</Stack>
					) : (
						<SuggestedEmptyView />
					)}
				</Flex>
			</Flex>
		</StaggeredEntrance>
	);
};
